import { ChevronLeft, ChevronRight } from 'lucide-react'
import type { ReactNode } from 'react'

import { cn } from '@/lib/cn'
import type { PaginationItem } from '@/lib/pagination'

interface PaginationProps {
  currentPage: number
  totalPages: number
  onPageChange: (page: number) => void
  disabled?: boolean
  pageItems: PaginationItem[]
  previousLabel?: string
  nextLabel?: string
  summary?: ReactNode
  className?: string
  align?: 'center' | 'between'
}

export function Pagination({
  currentPage,
  totalPages,
  onPageChange,
  disabled = false,
  pageItems,
  previousLabel = '上一页',
  nextLabel = '下一页',
  summary,
  className,
  align = 'between',
}: PaginationProps) {
  if (totalPages <= 1) {
    return null
  }

  return (
    <div className={cn('pagination', align === 'center' ? 'pagination--center' : '', className)}>
      {summary ? <div className="pagination__summary">{summary}</div> : <div />}
      <div className="pagination__controls">
        <button
          className="pagination__control"
          disabled={disabled || currentPage <= 1}
          onClick={() => onPageChange(Math.max(1, currentPage - 1))}
          type="button"
        >
          <ChevronLeft size={16} />
          <span>{previousLabel}</span>
        </button>

        {pageItems.map((item, index) =>
          item === 'ellipsis' ? (
            <span key={`ellipsis-${index}`} className="pagination__ellipsis">
              ...
            </span>
          ) : (
            <button
              key={item}
              className={cn('pagination__page', item === currentPage && 'is-active')}
              disabled={disabled}
              onClick={() => onPageChange(item)}
              type="button"
            >
              {item}
            </button>
          )
        )}

        <button
          className="pagination__control"
          disabled={disabled || currentPage >= totalPages}
          onClick={() => onPageChange(Math.min(totalPages, currentPage + 1))}
          type="button"
        >
          <span>{nextLabel}</span>
          <ChevronRight size={16} />
        </button>
      </div>
    </div>
  )
}
